import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Feather, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import GoogleSignInButton from "@/components/GoogleSignInButton";

function formatDetail(d) {
  if (!d) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).join(" ").trim() || "Invalid input";
  if (d && typeof d.msg === "string") return d.msg;
  return String(d);
}

export function AuthShell({ title, subtitle, children, footer }) {
  return (
    <div className="min-h-screen bg-[#FAF9F5]">
      <div className="mx-auto grid min-h-screen max-w-6xl grid-cols-1 lg:grid-cols-2">
        {/* Left column — brand & pitch */}
        <div className="hidden flex-col justify-between border-r border-[#E6E2D8] p-10 lg:flex">
          <Link to="/" className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#1A1D20] text-[#FAF9F5]">
              <Feather className="h-4 w-4" />
            </div>
            <div>
              <div className="font-heading text-lg font-bold leading-none text-[#1A1D20]">
                Write It Easy AI
              </div>
              <div className="mt-1 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                AI writing assistant
              </div>
            </div>
          </Link>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-indigo-700">
              Better writing, in seconds
            </p>
            <h2 className="font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-[#1A1D20]">
              Paste your text.
              <br />
              <span className="text-indigo-700">Choose an action.</span> Done.
            </h2>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-600">
              Grammar, clarity, tone, length, and brainstorms — for beginners
              who just want their writing to sound better.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-slate-600">
              <li className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-emerald-500" />
                5 free rewrites every day — no credit card needed
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-indigo-500" />
                Upgrade to Pro for unlimited rewrites — $9/month
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-500" />
                Your history is saved and always yours
              </li>
            </ul>
          </div>
          <p className="text-[11px] text-slate-400">
            © {new Date().getFullYear()} Write It Easy AI
          </p>
        </div>

        {/* Right column — form */}
        <div className="flex items-center justify-center p-6 sm:p-10">
          <div className="w-full max-w-sm">
            <Link to="/" className="mb-6 flex items-center gap-2 lg:hidden">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#1A1D20] text-[#FAF9F5]">
                <Feather className="h-4 w-4" />
              </div>
              <span className="font-heading text-base font-bold text-[#1A1D20]">
                Write It Easy AI
              </span>
            </Link>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight text-[#1A1D20]">
              {title}
            </h1>
            <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
            <div className="mt-6">{children}</div>
            {footer && (
              <div className="mt-4 text-center text-sm text-slate-500">
                {footer}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/app");
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Welcome back"
      subtitle="Sign in to continue writing better."
      footer={
        <>
          New here?{" "}
          <Link
            to="/register"
            className="font-semibold text-indigo-700 hover:underline"
            data-testid="go-to-register"
          >
            Create an account
          </Link>
        </>
      }
    >
      <div className="space-y-4">
        <GoogleSignInButton
          label="Sign in with Google"
          testId="google-sign-in-button-login"
        />
        <div className="relative py-1 text-center">
          <span className="relative z-10 bg-white px-3 text-[10px] font-semibold uppercase tracking-widest text-slate-400">
            or with email
          </span>
          <span className="absolute inset-x-0 top-1/2 h-px bg-slate-200" />
        </div>
      </div>
      <form onSubmit={submit} className="mt-4 space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            data-testid="login-email-input"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="rounded-xl border-[#E6E2D8] bg-white focus-visible:ring-indigo-500/40"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            data-testid="login-password-input"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="rounded-xl border-[#E6E2D8] bg-white focus-visible:ring-indigo-500/40"
          />
        </div>
        <Button
          type="submit"
          data-testid="login-submit-button"
          disabled={loading}
          className="group h-11 w-full gap-2 rounded-full bg-[#1A1D20] text-sm font-semibold text-[#FAF9F5] hover:bg-[#2A2E3D]"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              Sign in
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </>
          )}
        </Button>
        <div className="pt-1 text-right">
          <Link
            to="/forgot-password"
            data-testid="forgot-password-link"
            className="text-xs font-medium text-slate-500 hover:text-indigo-700 hover:underline"
          >
            Forgot password?
          </Link>
        </div>
      </form>
    </AuthShell>
  );
}
